import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { QuotaService } from '../../../quotas/services/quota.service';
import { OepSignatureDialogComponent, OepSignatureDialogData } from '../../../../shared/components/oep-signature-dialog/oep-signature-dialog.component';
import { ApplicationItem } from '../../../quotas/models/application.interface';

export interface ModerateDialogData {
  application: ApplicationItem;
  action: 'APPROVE' | 'REJECT';
}

import { splitApiErrors } from '../../../../core/utils/api-format';

@Component({
  selector: 'app-moderate-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
  ],
  templateUrl: './moderate-dialog.component.html',
  styleUrls: ['./moderate-dialog.component.css']
})
export class ModerateDialogComponent {
  private quotaService = inject(QuotaService);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<ModerateDialogComponent>);
  private snackBar = inject(MatSnackBar);
  data = inject<ModerateDialogData>(MAT_DIALOG_DATA);

  loading = signal(false);
  errorMessage = signal('');
  fieldErrors = signal<Record<string, string>>({});

  organizationInn = '';
  personIdnp = '';
  reason = '';

  getFieldError(field: string): string {
    return this.fieldErrors()[field] || '';
  }

  clearFieldError(field: string): void {
    const errors = { ...this.fieldErrors() };
    delete errors[field];
    this.fieldErrors.set(errors);
  }

  onNumericInput(event: Event, field: 'personIdnp' | 'organizationInn'): void {
    const input = event.target as HTMLInputElement;
    const filtered = input.value.replace(/\D/g, '');
    input.value = filtered;
    this[field] = filtered;
    this.clearFieldError(field);
  }

  isFormValid(): boolean {
    if (!this.organizationInn || !this.personIdnp) return false;
    if (this.data.action === 'REJECT' && !this.reason) return false;
    return true;
  }

  submit(): void {
    if (!this.isFormValid()) return;

    this.errorMessage.set('');

    // Открываем диалог OEP подписи
    const signatureDialogRef = this.dialog.open(OepSignatureDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        organizationInn: this.organizationInn,
        personIdnp: this.personIdnp,
        title: this.data.action === 'APPROVE' ? 'Подпись для принятия' : 'Подпись для отклонения'
      } as OepSignatureDialogData
    });

    signatureDialogRef.afterClosed().subscribe(result => {
      if (result?.success && result.token) {
        this.moderateApplication(result.token);
      } else if (result?.fieldErrors) {
        this.fieldErrors.set(result.fieldErrors);
      }
    });
  }

  private moderateApplication(token: string): void {
    this.loading.set(true);

    this.quotaService.moderateApplication({
      action: this.data.action,
      applicationId: this.data.application.id,
      organizationInn: this.organizationInn,
      personIdnp: this.personIdnp,
      token: token,
      rejectionReason: this.reason || undefined
    }).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.snackBar.open(
            this.data.action === 'APPROVE' ? 'Заявка успешно принята' : 'Заявка успешно отклонена',
            'OK',
            { duration: 3000, horizontalPosition: 'center', verticalPosition: 'top' }
          );
          this.dialogRef.close({ success: true });
        } else {
          this.handleApiErrors(response);
        }
      },
      error: (error) => {
        console.error('Error moderating application:', error);
        if (error.error && Array.isArray(error.error.res)) {
          this.handleApiErrors(error.error);
        } else {
          this.errorMessage.set('Ошибка модерации заявки');
        }
        this.loading.set(false);
      }
    });
  }

  private handleApiErrors(response: { msg?: string; res?: Array<{ field: string; ru: string }> }): void {
    const { fieldErrors, message } = splitApiErrors(response, 'Ошибка модерации заявки');
    if (Object.keys(fieldErrors).length) {
      this.fieldErrors.set(fieldErrors);
    }
    if (message) {
      this.errorMessage.set(message);
    }
  }

  close(): void {
    this.dialogRef.close({ success: false });
  }
}
